/**
 * 헤더의 "해봄 체험하기" 버튼을 누르면 뜨는 요원 유형 선택 모달 (2026-09-12 체험하기 개편판, 포스터형 다크).
 * `?new=1` 쿼리가 있을 때만 쓰이고, 없으면 기존 ExhibitionPosterModal 이 뜬다(useNewPlayFlow).
 * 14세 미만은 `/play3`, 14세 이상은 `/play4`(개편판 요원 등록 카드). `BaseDialog` headerless 모드 + 직접 그린 닫기 버튼.
 * 랜딩의 LandingPlayPosterModal 과 같은 구성.
 */
import React from "react";
import { useRouter } from "next/router";
import BaseDialog from "./BaseDialog";

interface ExhibitionPosterModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const OPTIONS = [
    { path: "/play3" as const, title: "14세 미만 · 학생 요원", desc: "학교·학년·닉네임으로 등록해요", primary: true },
    { path: "/play4" as const, title: "14세 이상 · 일반 요원", desc: "이름·소속·연락처로 등록해요", primary: false },
];

const ExhibitionPosterModal: React.FC<ExhibitionPosterModalProps> = ({ isOpen, onClose }) => {
    const router = useRouter();

    const handleSelect = (path: "/play4" | "/play3") => {
        onClose();
        router.push(path);
    };

    return (
        <BaseDialog
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel="해봄 체험하기 — 요원 유형 선택"
            widthClassName="w-full sm:max-w-[350px]"
            // 모바일(sm 미만)은 풀스크린: 오버레이 여백 0 + 컨테이너 화면 높이·모서리 없음
            overlayClassName="fixed inset-0 z-[100] flex items-start justify-center bg-inverse/40 overflow-y-auto p-0 sm:py-10 sm:px-4"
            containerClassName="relative flex min-h-[100dvh] flex-col bg-inverse text-white overflow-hidden shadow-lg sm:mt-10 sm:min-h-0 sm:rounded-[20px]"
        >
            <button
                type="button"
                onClick={onClose}
                aria-label="닫기"
                data-autofocus
                className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-[8px] text-white/75 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
            >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
                    <path d="m5 5 10 10M15 5 5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
            </button>

            <div className="px-6 pb-4 pt-[calc(2.2rem+env(safe-area-inset-top))] text-center sm:pb-3.5 sm:pt-7">
                {/* `.text-32` 는 text-heading(진한 색)을 포함해 다크 배경에서 글자가 사라진다 → 크기만 직접 지정 */}
                <h2 className="text-[32px] font-bold leading-tight text-white sm:text-[27px]">
                    코딩이가 사라졌다.
                    <span className="block">-요원 긴급 모집-</span>
                </h2>
                <p className="mt-2.5 text-16 font-bold text-white/85 sm:mt-2 sm:text-15">
                    마지막 신호 위치 : <strong className="font-bold text-accent">하나 연구실 B17</strong>
                </p>
            </div>

            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
                src="/images/exhibition/coding-missing-poster.png"
                alt="하나 연구실 B17. 경찰복을 입은 코딩이가 연구실에서 달려가고 있다."
                width={424}
                height={368}
                className="block h-auto w-full sm:mx-auto sm:w-[250px] sm:rounded-[12px]"
            />

            <div className="flex grow flex-col justify-end px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-4 sm:mx-auto sm:w-[290px] sm:grow-0 sm:px-0 sm:pb-5 sm:pt-3.5">
                <p className="mb-2.5 text-center text-13 text-primary-300 sm:mb-2">요원 유형을 선택해 주세요</p>
                <div className="space-y-2.5 sm:space-y-2">
                    {OPTIONS.map((o) => (
                        <button
                            key={o.path}
                            type="button"
                            onClick={() => handleSelect(o.path)}
                            className={`flex h-16 w-full items-center sm:h-[58px] justify-between gap-3 rounded-[12px] pl-5 pr-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2 focus-visible:ring-offset-inverse ${
                                o.primary
                                    ? "bg-primary hover:bg-primary-hover"
                                    : "border border-primary-300/50 bg-white/[0.06] hover:bg-white/[0.12]"
                            }`}
                        >
                            <span className="flex flex-col gap-0.5">
                                <span className="text-16 font-bold text-white">{o.title}</span>
                                <span className={`text-12 ${o.primary ? "text-primary-200" : "text-primary-300"}`}>{o.desc}</span>
                            </span>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 ${o.primary ? "text-white" : "text-primary-300"}`} aria-hidden="true" focusable="false">
                                <path d="M5 12h14M13 6l6 6-6 6" />
                            </svg>
                        </button>
                    ))}
                </div>
            </div>
        </BaseDialog>
    );
};

export default ExhibitionPosterModal;
